import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Wallet, IndianRupee, Calendar, Download, Filter, FileText, Search } from 'lucide-react';
import autoTable from 'jspdf-autotable';

const EXPEDIFY_INFO = {
  name: 'Brainpan Digital Pvt Ltd',
  address: 'C-1184, G.F., Sushant Lok-1, Gurgaon, Haryana - 122002',
  gstin: '06AAHCB6895J1Z4',
  state: 'Haryana',
};

const PartnerCommissions = ({ embedded = false }) => {
  const { user, getAuthHeaders, API_URL } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [monthFilter, setMonthFilter] = useState('all');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const [commissionsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/partner/commissions${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`, {
          headers: getAuthHeaders()
        }),
        axios.get(`${API_URL}/api/partner/stats`, {
          headers: getAuthHeaders()
        })
      ]);
      setCommissions(commissionsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
      toast.error('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const exportToCSV = () => {
    const headers = ['Month', 'Client', 'Promo Code', 'Promo Discount', 'Plan Value', 'Commission Rate', 'Commission Earned', 'Status', 'Paid Date'];
    const rows = filteredCommissions.map(c => [
      formatMonth(c.month),
      c.client_name,
      c.promo_code || '',
      c.promo_discount ? `${c.promo_discount}%` : '',
      c.plan_value,
      c.commission_rate === 0 ? '₹300 flat' : `${(c.commission_rate * 100).toFixed(0)}%`,
      c.amount,
      c.status,
      c.paid_date || ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commission-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const uniqueMonths = [...new Set(commissions.map(c => c.month))].sort().reverse();

  const filteredCommissions = commissions.filter(c => {
    const matchesSearch = !searchTerm || c.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMonth = monthFilter === 'all' || c.month === monthFilter;
    return matchesSearch && matchesMonth;
  });

  const body = (
      <div className="space-y-6" data-testid="partner-commissions">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {embedded ? <div /> : (
            <div>
              <h1 className="text-2xl font-bold text-[#1A1A1A]">
                Commission Tracker
              </h1>
              <p className="text-sm text-[#64748B] mt-1">
                Track your earnings and payout history
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="border-[#E2E2DF]"
              onClick={exportToCSV}
              data-testid="export-csv"
            >
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#ef785b]/10 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-[#ef785b]" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Total Earned</p>
                  <p className="text-xl font-bold text-[#1A1A1A]" data-testid="total-earned">
                    {formatCurrency(stats?.total_commission || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">This Month</p>
                  <p className="text-xl font-bold text-green-600" data-testid="this-month">
                    {formatCurrency(stats?.this_month_commission || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Next Payout</p>
                  <p className="text-xl font-bold text-amber-600">
                    {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 5).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Pending</p>
                  <p className="text-xl font-bold text-blue-600" data-testid="pending">
                    {formatCurrency(stats?.pending_commission || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card className="bg-white border-[#E2E2DF]">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  placeholder="Search by client name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10 border-[#E2E2DF]"
                />
              </div>
              <Select value={monthFilter} onValueChange={setMonthFilter}>
                <SelectTrigger className="w-44 h-10 border-[#E2E2DF]">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Months</SelectItem>
                  {uniqueMonths.map(m => (
                    <SelectItem key={m} value={m}>{formatMonth(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 h-10 border-[#E2E2DF]" data-testid="filter-commission-status">
                  <Filter className="w-4 h-4 mr-2 text-[#64748B]" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Commission Table */}
        <Card className="bg-white border-[#E2E2DF] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8] hover:bg-[#FAFAF8]">
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Month</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Client</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Promo Code</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Plan Value</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Commission %</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Commission Earned</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Paid Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ef785b]"></div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredCommissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-[#64748B]">
                      No commission records found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCommissions.map((commission, index) => (
                    <TableRow key={commission.id} className="hover:bg-[#FAFAF8]/50" data-testid={`commission-row-${index}`}>
                      <TableCell className="font-medium text-[#1A1A1A]">{formatMonth(commission.month)}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">{commission.client_name}</TableCell>
                      <TableCell className="text-sm text-[#64748B]">
                        {commission.promo_code ? (
                          <span className="px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-xs font-medium">
                            {commission.promo_code} ({commission.promo_discount}% off)
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-sm text-[#1A1A1A]">{formatCurrency(commission.plan_value)}</TableCell>
                      <TableCell className="text-sm text-[#1A1A1A]">
                        {commission.commission_rate === 0 ? '₹300 flat' : `${(commission.commission_rate * 100).toFixed(0)}%`}
                      </TableCell>
                      <TableCell className="font-medium text-green-600">{formatCurrency(commission.amount)}</TableCell>
                      <TableCell>
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            commission.status === 'paid'
                              ? 'bg-green-100 text-green-700 border border-green-200'
                              : commission.status === 'held'
                              ? 'bg-slate-100 text-slate-600 border border-slate-200'
                              : 'bg-amber-100 text-amber-700 border border-amber-200'
                          }`}
                          title={commission.status === 'held'
                            ? 'Earned before your account was active. Released once an admin approves you.'
                            : undefined}
                        >
                          {commission.status === 'paid' ? 'Paid' : commission.status === 'held' ? 'Held' : 'Pending'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-[#64748B]">
                        {commission.paid_date ? new Date(commission.paid_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
  );

  return embedded ? body : <DashboardLayout>{body}</DashboardLayout>;
};

export default PartnerCommissions;
